/**
 * Helpers que se inyectan en el scope donde se eval-ua tests_code.
 *
 * Convenciones del nuevo formato:
 *   - test(name, fn) → returns { name, fn } (azúcar para no escribir el objeto)
 *   - fn que tira error = test failed (mensaje del error en panel)
 *   - fn que retorna false explícitamente = test failed
 *   - fn que retorna true / undefined / otro truthy = test passed
 *
 * Backward compat con formato legacy:
 *   - Tests viejos `{ name, fn: () => boolean }` siguen andando.
 *   - El runtime trata `return false` como fail, todo lo demás como pass.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Helpers = ReturnType<typeof buildHelpers>;

export function buildHelpers() {
  return {
    /** Azúcar: `test("name", fn)` en vez de `{ name, fn }`. */
    test(name: string, fn: (...args: unknown[]) => unknown) {
      return { name, fn };
    },

    /** Assertions estilo Jest. Tiran error con mensaje legible si fallan. */
    expect(actual: unknown) {
      return {
        toBe(expected: unknown) {
          if (!Object.is(actual, expected)) {
            throw new AssertionError(
              `Esperaba ${formatValue(expected)} pero recibí ${formatValue(actual)}`
            );
          }
        },
        toEqual(expected: unknown) {
          if (!deepEqual(actual, expected)) {
            throw new AssertionError(
              `Esperaba ${formatValue(expected)} pero recibí ${formatValue(actual)}`
            );
          }
        },
        toBeTrue() {
          if (actual !== true) {
            throw new AssertionError(
              `Esperaba true pero recibí ${formatValue(actual)}`
            );
          }
        },
        toBeFalse() {
          if (actual !== false) {
            throw new AssertionError(
              `Esperaba false pero recibí ${formatValue(actual)}`
            );
          }
        },
        toBeGreaterThan(n: number) {
          if (typeof actual !== "number" || !(actual > n)) {
            throw new AssertionError(
              `Esperaba > ${n} pero recibí ${formatValue(actual)}`
            );
          }
        },
        toBeLessThan(n: number) {
          if (typeof actual !== "number" || !(actual < n)) {
            throw new AssertionError(
              `Esperaba < ${n} pero recibí ${formatValue(actual)}`
            );
          }
        },
        toBeDefined() {
          if (actual === undefined) {
            throw new AssertionError(`Esperaba un valor pero recibí undefined`);
          }
        },
        toThrow() {
          if (typeof actual !== "function") {
            throw new AssertionError(
              `toThrow() requiere una función, recibí ${formatValue(actual)}`
            );
          }
          try {
            (actual as () => unknown)();
            throw new AssertionError(
              `Esperaba que la función tirara error, pero no tiró nada`
            );
          } catch (e) {
            if (e instanceof AssertionError) throw e;
            // ok — tiró algo
          }
        },
      };
    },

    /** Mide cuánto tardó fn() y tira error si pasa de ms. */
    measureUnder(maxMs: number, fn: () => void) {
      const start = performance.now();
      fn();
      const elapsed = performance.now() - start;
      if (elapsed >= maxMs) {
        throw new AssertionError(
          `Tardó ${elapsed.toFixed(1)}ms (máximo permitido: ${maxMs}ms)`
        );
      }
    },
  };
}

// Marca custom para distinguir nuestros errores de assertion.
// Los runtimes podrían darle formato distinto a uno vs otro.
class AssertionError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = "AssertionError";
  }
}

function formatValue(v: unknown): string {
  if (typeof v === "string") return JSON.stringify(v);
  if (typeof v === "number" || typeof v === "boolean" || v == null) {
    return String(v);
  }
  if (typeof v === "function") return "<function>";
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== typeof b) return false;
  if (a == null || b == null) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((x, i) => deepEqual(x, b[i]));
  }
  if (typeof a === "object" && typeof b === "object") {
    const aKeys = Object.keys(a as object).sort();
    const bKeys = Object.keys(b as object).sort();
    if (aKeys.length !== bKeys.length) return false;
    if (!aKeys.every((k, i) => k === bKeys[i])) return false;
    return aKeys.every((k) =>
      deepEqual(
        (a as Record<string, unknown>)[k],
        (b as Record<string, unknown>)[k]
      )
    );
  }
  return false;
}
